import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import * as db from "@/lib/db";

const ProductFormSheet = ({
  open,
  onClose,
  onSuccess,
  product,
  categories,
  units,
  onCategoriesUpdate,
  onUnitsUpdate,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    unit: "",
    unitCost: "",
    defaultPrice: "",
    isActive: true,
    remark: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Category/Unit dialogs
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newUnitName, setNewUnitName] = useState("");

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        category: product.category || "",
        unit: product.unit || "",
        unitCost: product.unitCost !== undefined ? product.unitCost.toString() : "",
        defaultPrice: product.defaultPrice !== undefined && product.defaultPrice !== null ? product.defaultPrice.toString() : "",
        isActive: product.isActive !== undefined ? product.isActive : true,
        remark: product.remark || "",
      });
    } else {
      setFormData({
        name: "",
        category: "",
        unit: "",
        unitCost: "",
        defaultPrice: "",
        isActive: true,
        remark: "",
      });
    }
    setErrors({});
  }, [product, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Product name is required";
    }
    if (!formData.category.trim()) {
      newErrors.category = "Category is required";
    }
    if (!formData.unit.trim()) {
      newErrors.unit = "Unit is required";
    }
    if (!formData.unitCost || parseFloat(formData.unitCost) < 0) {
      newErrors.unitCost = "Unit cost must be 0 or greater";
    }
    if (formData.defaultPrice !== "" && parseFloat(formData.defaultPrice) < 0) {
      newErrors.defaultPrice = "Default price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        unit: formData.unit.trim(),
        unitCost: parseFloat(formData.unitCost),
        defaultPrice: formData.defaultPrice ? parseFloat(formData.defaultPrice) : null,
        isActive: formData.isActive,
        remark: formData.remark.trim() || null,
      };

      if (product) {
        await db.updateProduct(product.id, payload);
        toast.success("Product updated successfully");
      } else {
        await db.createProduct(payload);
        toast.success("Product created successfully");
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving product:", error);
      const errorMsg = error.message || "Failed to save product";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }

    try {
      await db.createCategory(newCategoryName.trim());
      toast.success("Category added successfully");
      setNewCategoryName("");
      setShowCategoryDialog(false);
      onCategoriesUpdate();
      setFormData({ ...formData, category: newCategoryName.trim() });
    } catch (error) {
      console.error("Error adding category:", error);
      const errorMsg = error.message || "Failed to add category";
      toast.error(errorMsg);
    }
  };

  const handleAddUnit = async () => {
    if (!newUnitName.trim()) {
      toast.error("Unit name cannot be empty");
      return;
    }

    try {
      await db.createUnit(newUnitName.trim());
      toast.success("Unit added successfully");
      setNewUnitName("");
      setShowUnitDialog(false);
      onUnitsUpdate();
      setFormData({ ...formData, unit: newUnitName.trim() });
    } catch (error) {
      console.error("Error adding unit:", error);
      const errorMsg = error.message || "Failed to add unit";
      toast.error(errorMsg);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right" className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-semibold text-[#193f7d]" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="form-title">
              {product ? "Edit Product" : "Add New Product"}
            </SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            {/* Product Name */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
                Product Name *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 px-4 text-base"
                placeholder="Enter product name"
                data-testid="product-name-input"
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* Category */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
                Category *
              </Label>
              <div className="flex gap-2">
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 flex-1" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={() => setShowCategoryDialog(true)}
                  variant="outline"
                  className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-xl h-12 px-4"
                  data-testid="add-category-btn"
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
              {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
            </div>

            {/* Unit */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
                Unit *
              </Label>
              <div className="flex gap-2">
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                  <SelectTrigger className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 flex-1" data-testid="unit-select">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.name}>
                        {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={() => setShowUnitDialog(true)}
                  variant="outline"
                  className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-xl h-12 px-4"
                  data-testid="add-unit-btn"
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
              {errors.unit && <p className="text-xs text-red-500 mt-1">{errors.unit}</p>}
            </div>

            {/* Unit Cost */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
                Unit Cost (RM) *
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.unitCost}
                onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 px-4 text-base"
                placeholder="0.00"
                data-testid="unit-cost-input"
              />
              {errors.unitCost && <p className="text-xs text-red-500 mt-1">{errors.unitCost}</p>}
            </div>

            {/* Default Selling Price */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
                Default Selling Price (RM)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.defaultPrice}
                onChange={(e) => setFormData({ ...formData, defaultPrice: e.target.value })}
                className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 px-4 text-base"
                placeholder="0.00 (optional)"
                data-testid="default-price-input"
              />
              {errors.defaultPrice && <p className="text-xs text-red-500 mt-1">{errors.defaultPrice}</p>}
            </div>

            {/* Remark */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
                Remark
              </Label>
              <Textarea
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl px-4 py-3 text-base min-h-[80px]"
                placeholder="Optional notes..."
                data-testid="remark-input"
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between py-4 px-5 bg-slate-50 rounded-xl">
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Active Status
              </Label>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                data-testid="active-status-switch"
              />
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1 border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-full h-12 font-medium"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-12 font-semibold transition-transform active:scale-95"
                data-testid="save-btn"
              >
                {submitting ? "Saving..." : product ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Add Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#193f7d]" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="add-category-dialog-title">
              Add New Category
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Create a new product category for your products.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
              Category Name
            </Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 px-4 text-base"
              placeholder="Enter category name"
              data-testid="new-category-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setShowCategoryDialog(false)}
              variant="outline"
              className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-full h-12 px-6"
              data-testid="cancel-category-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCategory}
              className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-12 px-6 font-semibold"
              data-testid="confirm-add-category-btn"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Unit Dialog */}
      <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#193f7d]" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="add-unit-dialog-title">
              Add New Unit
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Create a new unit of measurement for your products.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2 block">
              Unit Name
            </Label>
            <Input
              value={newUnitName}
              onChange={(e) => setNewUnitName(e.target.value)}
              className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 px-4 text-base"
              placeholder="Enter unit name"
              data-testid="new-unit-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setShowUnitDialog(false)}
              variant="outline"
              className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-full h-12 px-6"
              data-testid="cancel-unit-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddUnit}
              className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-12 px-6 font-semibold"
              data-testid="confirm-add-unit-btn"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductFormSheet;